import React, { useState } from 'react';
import PieChart from './PieChart';

const PeopleDrilldownView = ({ people, capabilities, coverageCalculator }) => {
  const [selectedPerson, setSelectedPerson] = useState(null);
  
  const getPersonAnalysis = (person) => {
    const analysis = {
      person,
      capabilityReadiness: [],
      totalCoverage: 0,
      skillGaps: [],
      strengths: [],
      developmentPriority: [],
      certificationAlignment: {}
    };

    // Analyze each capability
    capabilities.forEach(capability => {
      const coverage = coverageCalculator.calculateCoverage(person, capability);
      const missingSkills = coverageCalculator.getMissingSkills(person, capability);
      const matchingSkills = coverageCalculator.getMatchingSkills(person, capability);
      
      const readiness = {
        capability,
        coverage,
        missingSkills,
        matchingSkills,
        readinessLevel: coverage >= 80 ? 'Ready' : coverage >= 50 ? 'Developing' : 'Beginner',
        priority: missingSkills.length > 0 ? 'High' : 'Low'
      };

      analysis.capabilityReadiness.push(readiness);

      // Collect skill gaps
      missingSkills.forEach(skill => {
        if (!analysis.skillGaps.find(gap => gap.skill === skill)) {
          analysis.skillGaps.push({
            skill,
            capabilities: [capability.name],
            impact: 'High'
          });
        } else {
          const existing = analysis.skillGaps.find(gap => gap.skill === skill);
          existing.capabilities.push(capability.name);
        }
      });

      // Collect strengths
      matchingSkills.forEach(skill => {
        if (!analysis.strengths.find(strength => strength.skill === skill)) {
          analysis.strengths.push({
            skill,
            capabilities: [capability.name]
          });
        } else {
          const existing = analysis.strengths.find(strength => strength.skill === skill);
          existing.capabilities.push(capability.name);
        }
      });
    });

    // Calculate overall coverage
    analysis.totalCoverage = Math.round(
      analysis.capabilityReadiness.reduce((sum, cap) => sum + cap.coverage, 0) / 
      capabilities.length
    );

    // Prioritize development areas
    analysis.developmentPriority = analysis.skillGaps
      .sort((a, b) => b.capabilities.length - a.capabilities.length)
      .slice(0, 5);

    // Analyze certification alignment
    capabilities.forEach(capability => {
      if (capability.recommended_certifications) {
        capability.recommended_certifications.forEach(cert => {
          const hasCert = person.certifications && person.certifications.includes(cert);
          if (!analysis.certificationAlignment[capability.name]) {
            analysis.certificationAlignment[capability.name] = [];
          }
          analysis.certificationAlignment[capability.name].push({
            certification: cert,
            status: hasCert ? 'Achieved' : 'Recommended'
          });
        });
      }
    });

    return analysis;
  };

  const PersonDetailModal = ({ analysis, onClose }) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-7xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <div className="flex items-center space-x-3">
                <h2 className="text-2xl font-bold text-gray-900">{analysis.person.name}</h2>
                {analysis.person.country && (
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                    {analysis.person.country}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 mt-1">Individual Skill & Capability Assessment</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Overall Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="text-center">
              <PieChart percentage={analysis.totalCoverage} size={80} />
              <p className="text-sm text-gray-600 mt-2 font-medium">Overall Readiness</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{analysis.person.skills?.length || 0}</div>
              <p className="text-sm text-gray-600">Current Skills</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">{analysis.skillGaps.length}</div>
              <p className="text-sm text-gray-600">Skill Gaps</p>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{analysis.person.certifications?.length || 0}</div>
              <p className="text-sm text-gray-600">Certifications</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Capability Readiness Matrix */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Capability Readiness Matrix</h3>
              <div className="space-y-4">
                {analysis.capabilityReadiness.map((readiness, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-gray-900">{readiness.capability.name}</h4>
                      <div className="flex items-center space-x-2">
                        <PieChart percentage={readiness.coverage} size={32} strokeWidth={3} />
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          readiness.readinessLevel === 'Ready' 
                            ? 'bg-green-100 text-green-800'
                            : readiness.readinessLevel === 'Developing'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {readiness.readinessLevel}
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-green-700 mb-1">Current Skills ({readiness.matchingSkills.length})</p>
                        <div className="space-y-1">
                          {readiness.matchingSkills.map((skill, i) => (
                            <div key={i} className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">
                              {skill}
                            </div>
                          ))}
                          {readiness.matchingSkills.length === 0 && (
                            <div className="text-xs text-gray-500 italic">No current skills</div>
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-xs font-medium text-red-700 mb-1">Missing Skills ({readiness.missingSkills.length})</p>
                        <div className="space-y-1">
                          {readiness.missingSkills.map((skill, i) => (
                            <div key={i} className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded">
                              {skill}
                            </div>
                          ))}
                          {readiness.missingSkills.length === 0 && (
                            <div className="text-xs text-green-600 font-medium">✓ All skills covered</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Development Recommendations */}
            <div className="space-y-6">
              {/* Priority Skills to Develop */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Priority Skills Development</h3>
                <div className="space-y-3">
                  {analysis.developmentPriority.map((gap, index) => (
                    <div key={index} className="border border-orange-200 rounded-lg p-3 bg-orange-50">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-orange-900">{gap.skill}</h4>
                        <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                          {gap.capabilities.length} capabilities
                        </span>
                      </div>
                      <p className="text-xs text-orange-700">
                        Required for: {gap.capabilities.join(', ')}
                      </p>
                    </div>
                  ))}
                  {analysis.developmentPriority.length === 0 && (
                    <div className="text-center py-4 text-gray-500">
                      <div className="text-4xl mb-2">🎉</div>
                      <p>No immediate skill gaps identified!</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Current Strengths */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Current Strengths</h3>
                <div className="space-y-2">
                  {analysis.strengths.slice(0, 8).map((strength, index) => (
                    <div key={index} className="border border-green-200 rounded-lg p-2 bg-green-50">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-green-900 text-sm">{strength.skill}</h4>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          {strength.capabilities.length} caps
                        </span>
                      </div>
                      <p className="text-xs text-green-700 mt-1">
                        Supports: {strength.capabilities.slice(0, 2).join(', ')}
                        {strength.capabilities.length > 2 && ` +${strength.capabilities.length - 2} more`}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Certification Recommendations */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Certification Alignment</h3>
                <div className="space-y-3">
                  {Object.entries(analysis.certificationAlignment).map(([capabilityName, certs]) => (
                    <div key={capabilityName} className="border border-gray-200 rounded-lg p-3">
                      <h4 className="font-medium text-gray-900 mb-2">{capabilityName}</h4>
                      <div className="flex flex-wrap gap-2">
                        {certs.map((cert, index) => (
                          <span key={index} className={`text-xs px-2 py-1 rounded-full ${
                            cert.status === 'Achieved'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-600 border border-dashed border-gray-400'
                          }`}>
                            {cert.certification} {cert.status === 'Achieved' ? '✓' : '○'}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!people.length || !capabilities.length) {
    return (
      <div className="bg-white shadow rounded-lg p-6 h-full flex flex-col">
        <h2 className="text-lg font-medium text-gray-900 mb-4">People Development Dashboard</h2>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-8">
            <div className="text-gray-400 text-6xl mb-4">👥</div>
            <p className="text-gray-500 mb-4">No data available to display</p>
            <p className="text-sm text-gray-400">Add people and capabilities in the Editor to see individual development plans</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg h-full flex flex-col">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">People Development Dashboard</h2>
        <p className="text-sm text-gray-500 mt-1">
          Individual skill assessments and development recommendations
        </p>
      </div>

      <div className="flex-1 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {people.map((person, index) => {
            const analysis = getPersonAnalysis(person);
            const readyCapabilities = analysis.capabilityReadiness.filter(c => c.coverage >= 80).length;
            const developingCapabilities = analysis.capabilityReadiness.filter(c => c.coverage >= 50 && c.coverage < 80).length;
            
            return (
              <div
                key={index}
                onClick={() => setSelectedPerson(analysis)}
                className="border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-lg transition-shadow bg-white"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <h3 className="font-semibold text-gray-900">{person.name}</h3>
                    {person.country && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                        {person.country}
                      </span>
                    )}
                  </div>
                  <PieChart percentage={analysis.totalCoverage} size={40} strokeWidth={3} />
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Ready:</span>
                    <span className="font-medium text-green-600">{readyCapabilities} capabilities</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Developing:</span>
                    <span className="font-medium text-yellow-600">{developingCapabilities} capabilities</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Skills:</span>
                    <span className="font-medium">{person.skills?.length || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Skill gaps:</span>
                    <span className="font-medium text-red-600">{analysis.skillGaps.length}</span>
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-3">
                  <div className="text-xs text-blue-600 font-medium">Click for detailed analysis →</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedPerson && (
        <PersonDetailModal
          analysis={selectedPerson}
          onClose={() => setSelectedPerson(null)}
        />
      )}
    </div>
  );
};

export default PeopleDrilldownView;